import React from 'react';
import { ChevronsDown, ChevronsUp, MessageSquare } from 'react-feather';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { Button, ButtonGroup, FormGroup, Input, Label } from 'reactstrap';

import type { CalibrationRecommendation } from '../types';
import {
  getRecommendationDetails,
  RECOMMENDATION_FILTER_ORDER,
} from '../utils/comments';

export type CommentFiltersProps = {
  participantUniqueId: number;
  activeRecommendations: Set<CalibrationRecommendation>;
  onSelectAllRecommendations: () => void;
  onToggleRecommendation: (filter: CalibrationRecommendation) => void;
  showPrivateComments: boolean;
  onToggleShowPrivate: (value: boolean) => void;
  allThreadsExpanded: boolean;
  onToggleExpandCollapse: () => void;
  formatMessage: IntlShape['formatMessage'];
};

export const CommentFilters: React.FC<
  React.PropsWithChildren<CommentFiltersProps>
> = ({
  participantUniqueId,
  activeRecommendations,
  onSelectAllRecommendations,
  onToggleRecommendation,
  showPrivateComments,
  onToggleShowPrivate,
  allThreadsExpanded,
  onToggleExpandCollapse,
  formatMessage,
  children,
}) => {
  const showPrivateToggleId = `calibration-comments-show-private-${participantUniqueId}`;

  return (
    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
      <Button
        color="link"
        size="sm"
        className="px-0 d-inline-flex align-items-center gap-1"
        onClick={onToggleExpandCollapse}
      >
        {allThreadsExpanded ? (
          <ChevronsUp size={16} />
        ) : (
          <ChevronsDown size={16} />
        )}
        <FormattedMessage
          id="app.views.widgets.dashboards.calibration_dashboard.comments.toggle_threads"
          defaultMessage="{action}"
          values={{
            action: allThreadsExpanded
              ? formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.collapse',
                  defaultMessage: 'Collapse all',
                })
              : formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.expand',
                  defaultMessage: 'Expand all',
                }),
          }}
        />
      </Button>
      <FormGroup
        switch
        className="mb-0 d-flex align-items-center gap-2 text-nowrap"
      >
        <Input
          type="switch"
          id={showPrivateToggleId}
          checked={showPrivateComments}
          onChange={(event) => onToggleShowPrivate(event.target.checked)}
        />
        <Label check htmlFor={showPrivateToggleId} className="small mb-0">
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.comments.show_private"
            defaultMessage="Show private comments"
          />
        </Label>
      </FormGroup>
      <ButtonGroup
        size="sm"
        role="group"
        aria-label={formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.filter.aria_label',
          defaultMessage: 'Filter comments by recommendation',
        })}
      >
        <Button
          color="primary"
          outline={activeRecommendations.size !== 0}
          type="button"
          className="d-flex align-items-center justify-content-center"
          onClick={onSelectAllRecommendations}
          active={activeRecommendations.size === 0}
          aria-pressed={activeRecommendations.size === 0}
          title={formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments.filter.all',
            defaultMessage: 'All comments',
          })}
        >
          <MessageSquare size={16} aria-hidden="true" />
          <span className="visually-hidden">
            {formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.comments.filter.all',
              defaultMessage: 'All comments',
            })}
          </span>
        </Button>
        {RECOMMENDATION_FILTER_ORDER.map((filter) => {
          const isActive = activeRecommendations.has(filter);
          const filterDetails = getRecommendationDetails(formatMessage, filter);

          return (
            <Button
              key={filter}
              color="primary"
              outline={!isActive}
              type="button"
              className="d-flex align-items-center justify-content-center"
              onClick={() => onToggleRecommendation(filter)}
              active={isActive}
              aria-pressed={isActive}
              title={filterDetails.label}
            >
              {filterDetails.icon}
              <span className="visually-hidden">{filterDetails.label}</span>
            </Button>
          );
        })}
      </ButtonGroup>
      {children && (
        <div className="ms-auto d-inline-flex align-items-center gap-2 text-nowrap">
          {children}
        </div>
      )}
    </div>
  );
};
